<!-- src/views/Activity.vue -->
<template>
  <div style="background:black;overflow:hidden;">
    <!-- LOADER -->
    <v-container v-if="isLoading"
                 class="fill-height d-flex align-center justify-center">
      <v-progress-circular indeterminate color="#AC1726" size="64"/>
    </v-container>

    <!-- DASHBOARD -->
    <div v-else>
      <!-- WELCOME -->
      <v-container class="mb-5">
        <v-row class="text-center">
          <v-col cols="12">
            <v-card-title class="font-weight-bold"
                          style="color:#fff;font-size:1.5rem;">
              Welcome to your&nbsp;Nexus&nbsp;Dashboard
            </v-card-title>
          </v-col>
        </v-row>
      </v-container>

      <!-- ACTIVITY BLOCK -->
      <div class="pa-4"
           style="background:#fff;border-radius:20px 20px 0 0;min-height:70%;">
        <!-- BLOCK HEADER -->
        <v-row class="d-flex justify-space-between align-center mb-4">
          <div class="text-h4 font-weight-bold mx-6 my-4"
               style="color:#AC1726;text-transform:uppercase;letter-spacing:1px;
                      text-shadow:2px 2px 4px rgba(0,0,0,.2);">
            Activity
          </div>
        </v-row>

        <!-- EXPLANATORY NOTE -->
        <v-alert
          type="info"
          color="#fff3cd"
          text-color="#856404"
          border="left"
          class="mb-6"
          style="border-left-color: #ffeaa7 !important;"
        >
          <div style="color: #664d03;">
            <strong>Quick Access:</strong> This activity page is a fast way to find all the information currently open and projects currently being worked on by you. Each card shows your active items and current workload.
          </div>
        </v-alert>

        <!-- ACTIVITY CARDS - CSS Grid like Functions page -->
        <div class="functions-grid">
          <div 
            v-for="cat in filteredCategories"
            :key="cat.name"
            class="function-card" 
            @click="navigateToCategory(cat.name)"
          >
            <div class="function-card-content">
              <div class="function-icon">
                <v-icon :style="{ fontSize: '36px', color: '#4a5568' }">
                  {{ cat.icon }}
                </v-icon>
              </div>
              <div class="function-info">
                <h3>{{ cat.displayName }}</h3>
                <p>{{ getCardDescription(cat.name) }}</p>
              </div>
              <!-- Activity Badge -->
              <div class="activity-badge">
                {{ cat.count === null ? '…' : cat.count }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* ---------- Firebase ---------- */
import {
  getFirestore, collection, getDocs, doc, getDoc, query, where,
  /* Optional – available on firebase ≥ 9.23 */
  getCountFromServer
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { firebaseApp } from "@/firebaseConfig";

const db = getFirestore(firebaseApp);

export default {
  name: 'NexusDashboard',

  data() {
    return {
      /* tiles (badge starts with null → "…" in UI) */
      categories: [
        { name:'jobs',              displayName:'JOBS',               icon:'mdi-briefcase',             bgColor:'#AC1726', count:null },
        { name:'returns',           displayName:'RETURNS',            icon:'mdi-backspace',             bgColor:'#AC1726', count:null },
        { name:'deliveries',        displayName:'DELIVERIES',         icon:'mdi-car',                   bgColor:'#AC1726', count:null },
        { name:'purchaseOrders',    displayName:'PURCHASE ORDERS',    icon:'mdi-cash',                  bgColor:'#AC1726', count:null },
        { name:'engineerQuotes',    displayName:'ENGINEER QUOTES',    icon:'mdi-file-document-outline', bgColor:'#AC1726', count:null },
        { name:'salesOrderTracker', displayName:'SALES ORDER TRACKER',icon:'mdi-cart-outline',          bgColor:'#AC1726', count:null },
        { name:'awaitingSpares',    displayName:'AWAITING SPARES',    icon:'mdi-clock-outline',         bgColor:'#AC1726', count:null },
        { name:'samples',           displayName:'SAMPLES',            icon:'mdi-flask-outline',         bgColor:'#AC1726', count:null },
        { name:'shipment',          displayName:'SHIPMENTS',          icon:'mdi-truck-delivery',        bgColor:'#AC1726', count:null },
        { name:'serialNumber',      displayName:'SERIAL NUMBERS',     icon:'mdi-numeric',               bgColor:'#AC1726', count:null }
      ],

      userId:     null,
      userName:   '',
      userEmail:  '',
      userData:   {},

      isLoading:  true
    };
  },

  /* -------- PERMISSION FILTER -------- */
  computed: {
    filteredCategories() {
      return this.categories.filter(cat => {
        const perm =
          this.userData[cat.name] ||
          (cat.name === 'serialNumber' ? this.userData.serial : null);
        return perm &&
          (perm.View_in_Activity || perm.View_In_Activity ||
           perm.View_In_Activities || perm.View_in_Activities);
      });
    }
  },

  created() {
    this.initAuthWatcher();
  },

  methods: {
    /* ---------- CARD DESCRIPTIONS ---------- */
    getCardDescription(categoryName) {
      const descriptions = {
        jobs: 'Manage and track recon jobs',
        returns: 'Process and track all returns',
        deliveries: 'Track delivery and collection statuses',
        purchaseOrders: 'Create and manage purchase orders',
        engineerQuotes: 'Generate engineered quotes',
        salesOrderTracker: 'Monitor sales order progress',
        awaitingSpares: 'Track pending spare parts',
        samples: 'Manage and track all samples',
        shipment: 'Track and monitor all shipments',
        serialNumber: 'Manage product serial numbers'
      };
      return descriptions[categoryName] || 'Manage and track items';
    },

    /* ---------- INITIAL AUTH ---------- */
    initAuthWatcher() {
      const auth = getAuth(firebaseApp);
      onAuthStateChanged(auth, async user => {
        if (!user) { this.$router.push('/login'); return; }

        this.userId    = user.uid;
        this.userEmail = user.email.toLowerCase();

        try {
          const snap = await getDoc(doc(db,'users',this.userId));
          if (snap.exists()) {
            const d       = snap.data();
            this.userData = d;
            this.userName = d.fullName || '';
          }
        } catch(e){ console.error('User fetch failed',e); }

        this.isLoading = false;     // show dashboard immediately
        this.fetchCounts();         // start counting (async)
      });
    },

    /* ---------- USER-SPECIFIC COUNT FETCH FOR JOBS ---------- */
    async fetchJobsCount(email) {
      try {
        const snap = await getDocs(collection(db,'jobs'));
        const total = snap.docs.filter(d => {
          const data = d.data() || {};
          const assigned =
            (data.engineer?.email?.toLowerCase()   === email) ||
            (data.technician?.email?.toLowerCase() === email) ||
            (data.salesperson?.email?.toLowerCase()=== email);
          return assigned && data.status?.toLowerCase() !== 'completed';
        }).length;
        this.setCount('jobs', total);
      } catch (e) {
        console.error('Counting jobs failed:', e);
        this.setCount('jobs', 0);
      }
    },

    /* ---------- USER-SPECIFIC COUNT FETCH FOR RETURNS ---------- */
    async fetchReturnsCount(email) {
      try {
        const snap = await getDocs(collection(db,'returns'));
        const total = snap.docs.filter(d => {
          const data = d.data() || {};
          const assignedToUser = data.assignedTo?.email?.toLowerCase() === email;
          const notCompleted = data.returnStatus?.toLowerCase() !== 'completed';
          return assignedToUser && notCompleted;
        }).length;
        this.setCount('returns', total);
      } catch (e) {
        console.error('Counting returns failed:', e);
        this.setCount('returns', 0);
      }
    },

    /* ---------- USER-SPECIFIC COUNT FETCH FOR DELIVERIES ---------- */
    async fetchDeliveriesCount(email) {
      try {
        const snap = await getDocs(collection(db,'deliveries'));
        const total = snap.docs.filter(d => {
          const data = d.data() || {};
          const assigned =
            (data.engineer?.toLowerCase?.()   === email) ||
            (data.salesperson?.toLowerCase?.()=== email);
          return assigned && data.status?.toLowerCase() !== 'completed';
        }).length;
        this.setCount('deliveries', total);
      } catch (e) {
        console.error('Counting deliveries failed:', e);
        this.setCount('deliveries', 0);
      }
    },

    /* ---------- GENERIC COUNT HELPER ---------- */
    async quickCount({ col, name, wheres }) {
      try {
        let q = collection(db, col);
        wheres.forEach(w => q = query(q, where(...w)));

        let total = 0;
        if (typeof getCountFromServer === 'function') {
          const res = await getCountFromServer(q);
          total = res.data().count;
        } else {
          const snap = await getDocs(q);
          total = snap.size;
        }
        this.setCount(name, total);
      } catch (e) {
        console.error(`Counting ${name} failed:`, e);
        this.setCount(name, 0);
      }
    },

    setCount(name, value) {
      const idx = this.categories.findIndex(c => c.name === name);
      if (idx !== -1) this.$set(this.categories[idx], 'count', value);
    },

    /* ---------- USER-SPECIFIC COUNT FETCH FOR SALES ORDER TRACKER ---------- */
    async fetchSalesOrderTrackerCount(fullName) {
      try {
        const snap = await getDocs(collection(db,'SalesTracker'));
        const activeStatuses = ['Open Order', 'Referenced', 'Booked', 'Manufacturing', 'Partial Supply', 'In Transit', 'Docked', 'At Airport', 'On Route to JHB'];
        const total = snap.docs.filter(d => {
          const data = d.data() || {};
          const isAssignedSalesperson = data.salesperson?.fullName === fullName || data.salesperson === fullName;
          const hasActiveStatus = activeStatuses.includes(data.status);
          return isAssignedSalesperson && hasActiveStatus;
        }).length;
        this.setCount('salesOrderTracker', total);
      } catch (e) {
        console.error('Counting sales orders failed:', e);
        this.setCount('salesOrderTracker', 0);
      }
    },

    /* ---------- PARALLEL COUNT FETCH ---------- */
    async fetchCounts() {
      const email = this.userEmail;
      const uid   = this.userId;
      const fullName = this.userName;

      /* User-specific collections with manual filtering */
      const userSpecificTasks = [
        this.fetchJobsCount(email),
        this.fetchReturnsCount(email),
        this.fetchDeliveriesCount(email),
        this.fetchSalesOrderTrackerCount(fullName)
      ];

      /* Remaining collections via quickCount */
      const quickTasks = [
        ['purchaseOrders',   [['salespersonUID','==',uid],    ['status','!=','completed']]],
        ['engineerQuotes',   [['assignedTo.email','==',email],['status','!=','completed']]],
        ['awaitingSpares',   [['assignedTo.email','==',email],['status','!=','completed']]],
        ['samples',          [['assignedTo.email','==',email],['status','!=','completed']]],
        ['shipment',         [['assignedTo.email','==',email],['status','!=','completed']]],
        ['serialNumber',     [['assignedTo.email','==',email],['status','!=','completed']]]
      ].map(([col,wheres]) => this.quickCount({col,name:col,wheres}));

      await Promise.allSettled([ ...userSpecificTasks, ...quickTasks ]);
    },

    /* ---------- NAVIGATION ---------- */
    navigateToCategory(name) {
      const map = {
        jobs:'/activity/jobs',              returns:'/activity/returns',
        deliveries:'/activity/deliveries',       purchaseOrders:'/activity/purchase-orders',
        engineerQuotes:'/activity/engineer-quotes', salesOrderTracker:'/activity/sales-order-tracker',
        awaitingSpares:'/activity/awaiting-spares', samples:'/activity/samples',
        shipment:'/activity/shipment',           serialNumber:'/activity/serial-numbers'
      };
      this.$router.push(map[name] || '/');
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

body,div,span,v-card-title,v-row,v-col,v-card{font-family:"Montserrat",sans-serif;}

/* CSS Grid layout like Functions page */
.functions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.function-card {
  background: white;
  border-radius: 8px;
  padding: 0;
  transition: all 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  border: 1px solid #e0e6ed;
  position: relative;
}

.function-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 8px;
  background: #AC1726; /* Red color strip */
}

.function-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0,0,0,0.1);
  border-color: #ddd;
}

.function-card-content {
  padding: 30px 25px;
  display: flex;
  align-items: center;
  gap: 15px;
  min-height: 120px;
  position: relative;
}

.function-icon {
  min-width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.function-info h3 {
  font-size: 16px;
  color: #2c3e50;
  margin: 0 0 6px 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.function-info p {
  color: #7f8c8d;
  font-size: 13px;
  line-height: 1.4;
  margin: 0;
}

.activity-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  background: #AC1726;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .functions-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .functions-grid {
    grid-template-columns: 1fr;
  }
  
  .function-card-content {
    padding: 20px;
  }
  
  .activity-badge {
    width: 20px;
    height: 20px;
    font-size: 10px;
  }
}

/* Focus states for accessibility */
.function-card:focus {
  outline: 2px solid #AC1726;
  outline-offset: 2px;
}
</style>